// use-today-orchestration.ts — shared Today orchestration for BOTH UI themes (F7).
// Classic (today-view) and Aura (aura-today-view) render different layouts over the
// SAME derived data + handlers; this hook owns that shared state so the two views
// cannot drift apart. Pure derive logic stays in src/bento/*-derive modules.
import { useEffect, useMemo, useRef, useState } from "react";
import { todayIso } from "../lib/date";
import {
  clearFocusTimerSession,
  getDayCapacityMinutes,
  getFocusTimerSession,
  getMonthlyCloseDay,
  isGettingStartedDismissed,
  setGettingStartedDismissed,
} from "../lib/app-prefs";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task } from "../types";
import type { EntityKind } from "../bento/entity-form";
import { deriveToday } from "../bento/bento-derive";
import { isOverCapacity, plannedMinutes, todaysFocusTasks, todaysPlan } from "../bento/today-rituals-derive";
import { isWeeklyReviewDue } from "../bento/review-derive";
import { monthlyCloseStatus } from "../bento/finance-monthly-close-derive";
import { buildDigest } from "../bento/today-digest-derive";
import { buildInsights } from "../bento/insights-derive";
import { remainingSeconds } from "../bento/focus-timer-derive";
import {
  deriveGettingStarted,
  isGettingStartedAllDone,
  type GettingStartedStep,
} from "../bento/getting-started-derive";

export function useTodayOrchestration(
  api: LifeOsApi,
  onForm: (kind: EntityKind, item?: Record<string, unknown>) => void,
) {
  const today = todayIso();

  // deriveToday walks tasks/finance/habits/events/projects — memo on the exact
  // slices it reads (budget rollover math is the expensive part, F8).
  const d = useMemo(
    () => deriveToday(api.state),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api.state.tasks, api.state.transactions, api.state.recurring, api.state.budgets, api.state.habits, api.state.events, api.state.projects, api.state.accounts, api.state.currencyRates, today],
  );

  const focusTasks = useMemo(
    () => todaysFocusTasks(api.state, today),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api.state.tasks, api.state.dailyPlans, today],
  );

  // Memoize habit count — iterates habits array; only recomputes when habits change.
  const habitsToday = useMemo(
    () => api.state.habits.filter((h) => h.completions.includes(today)).length,
    [api.state.habits, today],
  );

  // ── Modal / banner visibility ───────────────────────────────────────────────
  const [showMorning, setShowMorning] = useState(false);
  const [showEvening, setShowEvening] = useState(false);
  const [showReview, setShowReview] = useState<null | "week" | "month">(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showReplanPrompt, setShowReplanPrompt] = useState(false);
  const [digestDismissed, setDigestDismissed] = useState(false);
  const [gsDismissed, setGsDismissed] = useState(() => isGettingStartedDismissed());

  // ── D35: focus timer — track which task (if any) has an active timer ────────
  const [focusTask, setFocusTask] = useState<Task | null>(null);

  // Cross-reload resume: a persisted session mounts the timer again. Once-guard ref
  // (same pattern as the recurring sweep) so this only runs on first render.
  const focusRestored = useRef(false);
  useEffect(() => {
    if (focusRestored.current) return;
    focusRestored.current = true;
    const saved = getFocusTimerSession();
    if (!saved) return;
    const task = api.state.tasks.find((t) => t.id === saved.taskId);
    const expired =
      remainingSeconds(saved.startedAt, saved.durationMin, saved.pausedAccum, Date.now(), saved.pausedAt) === 0;
    if (task && task.status !== "done" && !expired) {
      setFocusTask(task);
    } else {
      // Rotten session (task gone/done, or expired while the app was closed) — discard
      // instead of resurrecting an instant "Hết giờ" notification.
      clearFocusTimerSession();
    }
  }, [api.state.tasks]);

  function handleStartFocus(task: Task) {
    setFocusTask(task);
  }

  function handleFocusDone(taskId: string) {
    api.actions.updateTask(taskId, { status: "done" });
    setFocusTask(null);
  }

  function handleFocusStop() {
    setFocusTask(null);
  }

  /** Accumulate focus minutes onto the task — called by FocusTimer before session clears. */
  function handleFocusLogTime(taskId: string, minutes: number) {
    const current = api.state.tasks.find((t) => t.id === taskId)?.loggedMinutes ?? 0;
    api.actions.updateTask(taskId, { loggedMinutes: current + minutes });
  }

  // ── A1: getting-started checklist — derive step done-flags ──────────────────
  const gsSteps = deriveGettingStarted(api.state, today);
  const gsAllDone = isGettingStartedAllDone(gsSteps);
  // A brand-new EMPTY account always sees onboarding, even if getting-started was
  // dismissed before on THIS device (the dismiss flag is device-local, not per
  // account — without this a fresh account on a used browser lands on a blank app).
  const accountEmpty =
    api.state.tasks.length === 0 &&
    api.state.goals.length === 0 &&
    api.state.projects.length === 0 &&
    api.state.notes.length === 0;
  const showGettingStarted = !gsAllDone && (accountEmpty || !gsDismissed);

  function handleGsDismiss() {
    setGettingStartedDismissed();
    setGsDismissed(true);
  }

  function handleGsStepClick(step: GettingStartedStep) {
    if (step.done) return;
    if (step.actionKind === "task") onForm("task");
    else if (step.actionKind === "goal") onForm("goal");
    else setShowMorning(true);
  }

  // ── D22/D23 capacity + MIT lock; D30 weekly-review nudge ────────────────────
  const plan = todaysPlan(api.state, today);
  const capacity = getDayCapacityMinutes();
  // plannedMinutes iterates tasks — memo on mitTaskIds + tasks arrays.
  const planned = useMemo(
    () => plannedMinutes(plan?.mitTaskIds ?? [], api.state.tasks),
    [plan?.mitTaskIds, api.state.tasks],
  );
  const overCapacity = isOverCapacity(planned, capacity);
  const capacityPercent = capacity > 0 ? Math.min(100, Math.round((planned / capacity) * 100)) : 0;
  // isWeeklyReviewDue iterates weeklyReviews — memo on that array.
  const reviewDue = useMemo(
    () => isWeeklyReviewDue(api.state.weeklyReviews, today),
    [api.state.weeklyReviews, today],
  );

  // D47: trạng thái nhắc chốt tháng (tháng vừa khép, chưa chốt, đã tới ngày chốt).
  const closeStatus = monthlyCloseStatus(api.state, today, getMonthlyCloseDay());

  // A3: digest concern flags — cheap object build; memo avoids recreating object with same values.
  const digest = useMemo(
    () => buildDigest({
      overdueCount: d.overdue.length, overCapacity, reviewDue, inboxItems: api.state.inbox, today,
      monthClosePending: closeStatus.pending, monthCloseTarget: closeStatus.targetMonth,
      budgetAlertCount: d.budgetAlerts.length,
    }),
    [d.overdue.length, overCapacity, reviewDue, api.state.inbox, today, closeStatus.pending, closeStatus.targetMonth, d.budgetAlerts.length],
  );

  // Proactive insights — deterministic, no AI, capped at 5.
  const insights = useMemo(
    () => buildInsights(api.state, today),
    // Re-derive when finance/tasks/habits data changes (coarse dep — cheap to recompute).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api.state.transactions, api.state.recurring, api.state.tasks, api.state.habits, today],
  );

  // Modal nhập lý do thay window.prompt (native dialog phá theme + block JS thread).
  function handleReplan() {
    if (!plan) return;
    setShowReplanPrompt(true);
  }

  function submitReplan(reason: string) {
    if (!plan) return;
    api.actions.saveDailyPlan({ ...plan, locked: false, replanReason: reason });
    setShowMorning(true);
  }

  return {
    today, d, focusTasks, habitsToday,
    plan, capacity, planned, overCapacity, capacityPercent,
    reviewDue, closeStatus, digest, insights,
    gsSteps, showGettingStarted, handleGsDismiss, handleGsStepClick,
    digestDismissed, dismissDigest: () => setDigestDismissed(true),
    showMorning, setShowMorning,
    showEvening, setShowEvening,
    showReview, setShowReview,
    showSetup, setShowSetup,
    showReplanPrompt, setShowReplanPrompt, handleReplan, submitReplan,
    focusTask, handleStartFocus, handleFocusDone, handleFocusStop, handleFocusLogTime,
  };
}
